
// Base URL for the API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL_DEV || 'https://dev.api.fusshn.com'; // Fallback for local dev

/**
 * Generic helper for making API requests.
 * @param {string} endpoint - The API endpoint (relative to base URL).
 * @param {object} options - Fetch options (method, headers, body, etc.).
 * @returns {Promise<any>} - The JSON response data.
 */
async function request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const defaultHeaders = {
        'Content-Type': 'application/json',
        // Add auth token header here if needed in the future
    };

    const config = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    };

    if (config.body && typeof config.body === 'object') {
        config.body = JSON.stringify(config.body);
    }

    try {
        const response = await fetch(url, config);

        // Handle non-200 responses
        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || `API Error: ${response.status} ${response.statusText}`);
        }

        // Return JSON data
        // Some endpoints might return 204 No Content
        if (response.status === 204) {
            return null;
        }

        const data = await response.json();
        return data;

    } catch (error) {
        console.error(`API Request failed for ${endpoint}:`, error);
        throw error;
    }
}

/**
 * Service class for handling all backend API interactions.
 */
export const ApiService = {
    // --- User Management ---

    /**
     * Create a new user.
     * @param {object} userData - User data.
     */
    createUser: async (userData) => {
        return request('/user', {
            method: 'POST',
            body: userData,
        });
    },

    /**
     * Get all users.
     */
    getAllUsers: async () => {
        return request('/user', {
            method: 'GET',
        });
    },

    /**
     * Get a single user by UID.
     * @param {string} uid - User ID.
     */
    getSingleUser: async (uid) => {
        return request(`/user/${uid}`, {
            method: 'GET',
        });
    },

    /**
     * Update a user.
     * @param {string} uid - User ID.
     * @param {object} data - Fields to update.
     */
    updateUser: async (uid, data) => {
        return request(`/user/${uid}`, {
            method: 'PUT',
            body: data,
        });
    },

    /**
     * Delete a user.
     * @param {string} uid - User ID.
     */
    deleteUser: async (uid) => {
        return request(`/user/${uid}`, {
            method: 'DELETE',
        });
    },

    // --- Event Management ---

    /**
     * Get all events.
     */
    getAllEvents: async () => {
        return request('/events', {
            method: 'GET',
        });
    },

    /**
     * Get a single event.
     * @param {string} eventId 
     */
    getSingleEvent: async (eventId) => {
        return request(`/event/${eventId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all dates for an event.
     * @param {string} eventId 
     */
    getAllEventDates: async (eventId) => {
        return request(`/event/${eventId}/dates`, {
            method: 'GET',
        });
    },

    /**
     * Get a single event date.
     * @param {string} eventId 
     * @param {string} dateId 
     */
    getSingleEventDate: async (eventId, dateId) => {
        return request(`/event/${eventId}/date/${dateId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all slots for an event date.
     * @param {string} eventId 
     * @param {string} dateId 
     */
    getAllSlots: async (eventId, dateId) => {
        return request(`/event/${eventId}/date/${dateId}/slots`, {
            method: 'GET',
        });
    },

    /**
     * Get a single slot.
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    getSingleSlot: async (eventId, dateId, slotId) => {
        return request(`/event/${eventId}/date/${dateId}/slot/${slotId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all slots for an event (using new flat API).
     * @param {string} eventId 
     */
    getSlots: async (eventId) => {
        return request(`/slots?eventId=${eventId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all tickets for a slot.
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    getAllTickets: async (eventId, dateId, slotId) => {
        return request(`/event/${eventId}/date/${dateId}/slot/${slotId}/tickets`, {
            method: 'GET',
        });
    },

    /**
     * Get a single ticket.
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     * @param {string} ticketId 
     */
    getSingleTicket: async (eventId, dateId, slotId, ticketId) => {
        return request(`/event/${eventId}/date/${dateId}/slot/${slotId}/ticket/${ticketId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all event dates for an event (new API).
     * @param {string} eventId 
     */
    getEventDates: async (eventId) => {
        return request(`/eventDates?event_id=${eventId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all slots for an event date (new API).
     * @param {string} eventId 
     * @param {string} dateId 
     */
    getEventSlots: async (eventId, dateId) => {
        return request(`/slots?event_id=${eventId}&date_id=${dateId}`, {
            method: 'GET',
        });
    },

    /**
     * Get all tickets for a slot (new API).
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    getEventTickets: async (eventId, dateId, slotId) => {
        return request(`/tickets?event_id=${eventId}&date_id=${dateId}&slot_id=${slotId}`, {
            method: 'GET',
        });
    },

    /**
     * Update a ticket.
     * @param {string} ticketId 
     * @param {object} data 
     * @param {string} eventId 
     * @param {string} dateId 
     * @param {string} slotId 
     */
    updateTicket: async (ticketId, data, eventId, dateId, slotId) => {
        // Construct query params if needed or just use the ID
        // Based on apis_ref.md: 
        // Endpoint: PUT /ticket/{ticket_id}
        // Query Parameters: event_id, date_id, slot_id
        const queryParams = new URLSearchParams({
            event_id: eventId,
            date_id: dateId,
            slot_id: slotId
        }).toString();

        return request(`/ticket/${ticketId}?${queryParams}`, {
            method: 'PUT',
            body: data
        });
    },


    // --- Booking Management ---

    /**
     * Get all bookings.
     * @param {string[]} ids - Optional list of booking IDs to filter.
     */
    getAllBookings: async (ids = []) => {
        let endpoint = '/bookings';
        if (ids && ids.length > 0) {
            const queryString = ids.map(id => `ids=${id}`).join('&');
            endpoint += `?${queryString}`; // Check if backend supports this array format or comma separated
        }
        return request(endpoint, {
            method: 'GET',
        });
    },

    /**
     * Create a new booking.
     * @param {object} bookingData 
     */
    createBooking: async (bookingData) => {
        return request('/booking', {
            method: 'POST',
            body: bookingData,
        });
    },

    /**
     * Update a booking.
     * @param {string} bookingId 
     * @param {object} bookingData 
     */
    updateBooking: async (bookingId, bookingData) => {
        return request(`/booking/${bookingId}`, {
            method: 'PUT',
            body: bookingData,
        });
    },

    // --- Miscellaneous ---

    /**
     * Get all coupons.
     */
    getCoupons: async () => {
        return request('/coupons', {
            method: 'GET',
        });
    },

    /**
     * Get app versions.
     */
    getAppVersions: async () => {
        return request('/app-versions', {
            method: 'GET',
        });
    }
};
